// src/pages/draft-orders/DraftOrdersPage.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, subMonths, parseISO, formatISO } from "date-fns";
import { toDate } from "date-fns-tz";
import ReactDOM from "react-dom";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import API from "../../services/api";
import { API as ApiUrls } from "../../utils/apiUrls";
import {
  DraftOrderStatusEnum,
  getDraftOrderStatusName,
  DraftOrderSourceEnum,
} from "../../utils/enums";
import { formatInIST } from "../../utils/dateHelpers";

import CustomDateRangePicker from "../../utils/CustomDateRangePicker";
import { SigmaIcon, ExclaimIcon } from "../../icons/icon.jsx";

let _statusCountsCache = null;
let _statusCountsCacheTime = 0;
const STATUS_COUNTS_CACHE_TTL = 30000; // 30 seconds

/* ──────────────────────────── Courier EDD Helper ──────────────────────────── */
function formatExpectedDeliveryIST(maxTatDays) {
  if (maxTatDays == null || maxTatDays === "") return "-";
  const d = new Date();
  d.setDate(d.getDate() + Number(maxTatDays));
  return d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" });
}

/* ──────────────── Placement Error → User-Friendly Message ──────────────── */
function parsePlacementErrorMessage(rawMessage) {
  if (!rawMessage) return "Unknown error occurred";

  // Known Java exception / API error patterns → human-readable messages
  const patterns = [
    // Invoice details
    { match: /getOrderInvoiceDetails.*is null/i, msg: "Invoice details are missing. Please add invoice value before placing this order." },
    { match: /getInvoiceValue.*is null/i, msg: "Invoice value is missing. Please set the invoice value." },
    { match: /getInvoiceNumber.*is null/i, msg: "Invoice number is missing." },
    // Delivery / Drop details
    { match: /getDropDetails.*is null/i, msg: "Delivery address details are missing. Please add customer delivery information." },
    { match: /getDropPostalCode.*is null|drop_postal_code.*null/i, msg: "Delivery pincode is missing." },
    { match: /getDropPoc\b.*is null/i, msg: "Customer name is missing in delivery details." },
    { match: /getDropPocContactNumber.*is null/i, msg: "Customer phone number is missing in delivery details." },
    { match: /getDropAddress.*is null/i, msg: "Delivery address is missing." },
    { match: /getDropCity.*is null/i, msg: "Delivery city is missing." },
    { match: /getDropState.*is null/i, msg: "Delivery state is missing." },
    // Pickup details
    { match: /getPickupDetails.*is null/i, msg: "Pickup address details are missing. Please add pickup information." },
    { match: /getPickupPostalCode.*is null/i, msg: "Pickup pincode is missing." },
    { match: /getPickupAddress.*is null/i, msg: "Pickup address is missing." },
    { match: /getPickupPoc\b.*is null/i, msg: "Pickup contact person is missing." },
    // Shipment / Package details
    { match: /getShipmentDetails.*is null/i, msg: "Shipment details are missing." },
    { match: /getShipmentPackages.*is null|shipment_packages.*null/i, msg: "Package details are missing. Please add package dimensions and weight." },
    { match: /getDimensions.*is null/i, msg: "Package dimensions (length, breadth, height) are missing." },
    { match: /getWeight.*is null/i, msg: "Package weight is missing." },
    // Order details
    { match: /getOrderDetails.*is null/i, msg: "Order details are missing." },
    { match: /getOrderType.*is null/i, msg: "Payment mode (Prepaid/COD) is missing." },
    { match: /getOrderReferenceNumber.*is null/i, msg: "Order reference number is missing." },
    // Item details
    { match: /getItems.*is null|items.*null.*empty/i, msg: "Item details are missing. Please add at least one item." },
    // Carrier
    { match: /carrier.*not found|carrier.*invalid|getCarrier.*is null/i, msg: "Carrier details are invalid or missing." },
    // COD
    { match: /getCodAmount.*is null/i, msg: "COD amount is missing for a Cash on Delivery order." },
    // Common API error codes
    { match: /DUPLICATE_ORDER/i, msg: "An order with this reference number already exists. Please use a unique order reference." },
    { match: /CARRIER_NOT_SERVICEABLE/i, msg: "Selected carrier cannot service this route. Please choose a different carrier." },
    { match: /PINCODE_NOT_SERVICEABLE/i, msg: "Delivery pincode is not serviceable by any carrier." },
    // Generic NullPointerException (keep last among NPE patterns)
    { match: /NullPointerException|Cannot invoke.*because.*is null/i, msg: "Some required order details are missing. Please review and complete all mandatory fields." },
    { match: /PLACEMENT_EXCEPTION/i, msg: "Order placement failed due to missing or invalid data. Please review all required fields." },
  ];

  for (const { match, msg } of patterns) {
    if (match.test(rawMessage)) return msg;
  }

  // Long technical messages (stack traces) - simplify
  if (rawMessage.length > 200) {
    return "Order placement failed due to a technical error. Please review all mandatory fields and try again.";
  }

  return rawMessage;
}

/* ──────────────── Draft Order Mandatory Field Validation ──────────────── */
/**
 * Validates a draft order's `order_data` against the same mandatory fields
 * required by the Create Forward Order screen.
 * Returns an array of { field, section } objects for each missing/invalid field.
 * An empty array means the draft is valid for placement.
 */
function validateDraftOrderData(orderData) {
  if (!orderData) return [{ field: "Order Data", section: "General", message: "Order data is completely missing" }];

  const missing = [];
  const pickup = orderData.pickup_details || {};
  const drop = orderData.drop_details || {};
  const sd = orderData.shipment_details || {};
  const orderDet = sd.order_details || {};
  const invoiceDet = orderDet.order_invoice_details || {};
  // shipment_packages may be at shipment_details level or order_details level depending on source
  const pkgs = sd.shipment_packages || orderDet.shipment_packages || [];
  const firstPkg = pkgs[0] || {};
  const dims = firstPkg.dimensions || {};
  const weight = firstPkg.weight || {};
  const items = firstPkg.items || [];

  // ── Customer / Delivery Details ──
  if (!drop.drop_poc?.trim()) missing.push({ field: "Customer Name", section: "Delivery" });
  if (!drop.drop_poc_contact_number?.trim() || !/^[0-9]{10}$/.test(drop.drop_poc_contact_number?.trim()))
    missing.push({ field: "Customer Phone (10 digits)", section: "Delivery" });
  if (!drop.drop_address?.trim()) missing.push({ field: "Delivery Address", section: "Delivery" });
  if (!drop.drop_city?.trim()) missing.push({ field: "City", section: "Delivery" });
  if (!drop.drop_state?.trim()) missing.push({ field: "State", section: "Delivery" });
  if (!drop.drop_postal_code?.trim() || !/^[0-9]{6}$/.test(drop.drop_postal_code?.trim()))
    missing.push({ field: "Pincode (6 digits)", section: "Delivery" });

  // ── Pickup Details ──
  if (!pickup.pickup_address?.trim()) missing.push({ field: "Pickup Address", section: "Pickup" });
  if (!pickup.pickup_postal_code?.trim()) missing.push({ field: "Pickup Pincode", section: "Pickup" });

  // ── Invoice Details ──
  const invVal = invoiceDet.invoice_value;
  if (invVal == null || invVal === "" || !isFinite(Number(invVal)) || Number(invVal) <= 0)
    missing.push({ field: "Invoice Value", section: "Order" });

  // ── Payment Mode ──
  if (!orderDet.order_type?.trim()) missing.push({ field: "Payment Mode (Prepaid/COD)", section: "Order" });

  // ── Package Dimensions ──
  if (!dims.length || Number(dims.length) <= 0) missing.push({ field: "Length", section: "Package" });
  if (!dims.breadth || Number(dims.breadth) <= 0) missing.push({ field: "Breadth", section: "Package" });
  if (!dims.height || Number(dims.height) <= 0) missing.push({ field: "Height", section: "Package" });

  // ── Package Weight ──
  if (!weight.value || Number(weight.value) <= 0) missing.push({ field: "Weight", section: "Package" });

  // ── Item Details ──
  if (items.length === 0) {
    missing.push({ field: "At least one item required", section: "Items" });
  } else {
    items.forEach((item, idx) => {
      const prefix = items.length > 1 ? `Item ${idx + 1} - ` : "";
      if (!item.item_general_details?.item_name?.trim()) missing.push({ field: `${prefix}Item Name`, section: "Items" });
      if (!item.quantity || Number(item.quantity) <= 0) missing.push({ field: `${prefix}Quantity`, section: "Items" });
    });
  }

  return missing;
}

/* ──────────────────────────── SVG Icons ──────────────────────────── */
const DraftIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const QueueIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const PlacedIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const DiscardIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

/* ──────────────────────────── Tabs (5 statuses + All) ──────────────────────────── */
const TABS = [
  { key: "DRAFT", label: "Draft", Icon: DraftIcon, badgeColor: "bg-blue-600", color: "text-blue-600" },
  { key: "QUEUED", label: "Queued", Icon: QueueIcon, badgeColor: "bg-amber-600", color: "text-amber-600" },
  { key: "PLACED", label: "Placed", Icon: PlacedIcon, badgeColor: "bg-emerald-600", color: "text-emerald-600" },
  { key: "FAILED", label: "Failed", Icon: ExclaimIcon, badgeColor: "bg-rose-600", color: "text-rose-600" },
  { key: "DISCARDED", label: "Discarded", Icon: DiscardIcon, badgeColor: "bg-gray-500", color: "text-gray-500" },
  { key: "all", label: "All Drafts", Icon: SigmaIcon, badgeColor: "bg-slate-600", color: "text-slate-600" },
];

const ACTIONS_BY_STATUS = {
  DRAFT: ["view", "edit", "place", "duplicate", "discard", "permanentDelete"],
  QUEUED: ["view", "duplicate", "permanentDelete"],
  PLACED: ["view", "duplicate", "viewShipment"],
  FAILED: ["view", "edit", "retry", "duplicate", "discard", "permanentDelete"],
  DISCARDED: ["view", "duplicate", "permanentDelete"],
};

/* ──────────────────────────── Status Badge ──────────────────────────── */
const DraftStatusBadge = ({ status }) => {
  const style = {
    DRAFT: "bg-blue-100 text-blue-800 ring-blue-200",
    QUEUED: "bg-amber-100 text-amber-800 ring-amber-200",
    PLACED: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    FAILED: "bg-rose-100 text-rose-800 ring-rose-200",
    DISCARDED: "bg-gray-100 text-gray-800 ring-gray-200",
  }[status] || "bg-gray-100 text-gray-800 ring-gray-200";

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ring-1 ${style}`}>
      {getDraftOrderStatusName(status)}
    </span>
  );
};

/* ──────────────────────────── Source Badge ──────────────────────────── */
const SourceBadge = ({ source }) => {
  const style = {
    UI_PANEL: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    BULK_UPLOAD: "bg-purple-50 text-purple-700 ring-purple-200",
    SHOPIFY: "bg-green-50 text-green-700 ring-green-200",
    AMAZON: "bg-yellow-50 text-yellow-700 ring-yellow-200",
    MERCHANT_API: "bg-cyan-50 text-cyan-700 ring-cyan-200",
    UNICOMMERCE: "bg-teal-50 text-teal-700 ring-teal-200",
  }[source] || "bg-gray-50 text-gray-700 ring-gray-200";

  const label = Object.values(DraftOrderSourceEnum).find(e => e.code === source)?.name || source;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${style}`}>
      {label}
    </span>
  );
};

/* ──────────────────────────── Confirm Modal ──────────────────────────── */
const ConfirmModal = ({ open, title, message, onConfirm, onCancel, confirmLabel = "Confirm", variant = "danger" }) => {
  if (!open) return null;
  const btn = variant === "danger"
    ? "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
    : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700";
  const iconBg = variant === "danger" ? "bg-red-100" : "bg-blue-100";
  const iconClr = variant === "danger" ? "text-red-600" : "text-blue-600";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
            <svg className={`w-6 h-6 ${iconClr}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        </div>
        <div className="text-gray-600 mb-6 leading-relaxed">{message}</div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all">Cancel</button>
          <button onClick={() => { onConfirm(); onCancel(); }} className={`flex-1 px-4 py-2.5 ${btn} text-white rounded-xl font-semibold transition-all shadow-lg`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────── Toast ──────────────────────────── */
const Toast = ({ message, type = "success", onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const bg = type === "success" ? "bg-green-500" : type === "error" ? "bg-red-500" : "bg-blue-500";
  return (
    <div className={`fixed top-4 right-4 ${bg} text-white px-6 py-3 rounded-lg shadow-2xl z-50 animate-slide-in-right flex items-center gap-3`}>
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:bg-white/20 rounded p-1">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
      </button>
    </div>
  );
};

/* ──────────────────────────── Empty State ──────────────────────────── */
const EmptyState = ({ tab, onReset }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="w-32 h-32 mb-6 opacity-30">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-full h-full text-gray-400">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
    <h3 className="text-lg font-bold text-gray-700 mb-2">No draft orders found</h3>
    <p className="text-gray-500 mb-6 text-center max-w-md">
      {tab === "all"
        ? "No draft orders match your current filters. Try adjusting your search criteria."
        : `No draft orders in "${TABS.find(t => t.key === tab)?.label}" status.`}
    </p>
    <button onClick={onReset} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">Reset Filters</button>
  </div>
);

/* ──────────────────────────── Table Skeleton ──────────────────────────── */
const TableSkeleton = () => (
  <div className="space-y-3 p-4">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="animate-pulse flex space-x-4 bg-gray-50 p-4 rounded-lg">
        <div className="h-4 w-4 bg-gray-300 rounded" />
        <div className="flex-1 space-y-3">
          <div className="h-4 bg-gray-300 rounded w-3/4" />
          <div className="grid grid-cols-6 gap-4">
            {[...Array(6)].map((_, j) => <div key={j} className="h-3 bg-gray-300 rounded" />)}
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ──────────────────────── Action Menu (portal-based, status-aware) ──────────────────────── */
const ActionMenu = ({ draft, onAction, onOpenChange, validationErrors = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const allowedKeys = ACTIONS_BY_STATUS[draft.draft_status] || ["view"];
  const isRetryable = draft.draft_status === "FAILED" && draft.placement_error?.retryable;

  // Build visible actions based on status
  const ALL_ACTION_DEFS = [
    {
      key: "view", label: "View Details", color: "text-gray-700 hover:bg-gray-50",
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
    },
    {
      key: "place",
      label: validationErrors.length > 0 ? `Place Order (${validationErrors.length} field${validationErrors.length > 1 ? "s" : ""} missing)` : "Place Order",
      color: validationErrors.length > 0 ? "text-gray-400" : "text-emerald-700 hover:bg-emerald-50",
      icon: validationErrors.length > 0
        ? <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
        : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>,
    },
    {
      key: "retry", label: "Retry Placement", color: "text-amber-700 hover:bg-amber-50",
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
      disabled: !isRetryable,
    },
    {
      key: "edit", label: "Edit Draft", color: "text-blue-700 hover:bg-blue-50",
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
    },
    {
      key: "duplicate", label: "Duplicate", color: "text-purple-700 hover:bg-purple-50",
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>,
    },
    {
      key: "viewShipment", label: "View Shipment", color: "text-indigo-700 hover:bg-indigo-50",
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>,
    },
    {
      key: "discard", label: "Discard", color: "text-red-700 hover:bg-red-50",
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
    },
    {
      key: "permanentDelete",
      label: draft.draft_status === "DISCARDED" ? "Delete Permanently" : "Delete Permanently",
      color: "text-red-800 hover:bg-red-100 font-semibold",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.5 11.5l5 5m0-5l-5 5" />
        </svg>
      ),
    },
  ];

  const actions = ALL_ACTION_DEFS.filter(a => allowedKeys.includes(a.key));

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 224;
      const menuHeight = actions.length * 48;
      let top = rect.bottom + 4;
      let left = rect.right - menuWidth;
      if (top + menuHeight > window.innerHeight) top = rect.top - menuHeight - 4;
      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth) left = window.innerWidth - menuWidth - 8;
      setPosition({ top, left });
    }
    const next = !isOpen;
    setIsOpen(next);
    onOpenChange?.(next);
  };

  const closeMenu = useCallback(() => { setIsOpen(false); onOpenChange?.(false); }, [onOpenChange]);

  useEffect(() => {
    const h = (e) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target) && menuRef.current && !menuRef.current.contains(e.target)) closeMenu();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [isOpen, closeMenu]);

  const menuNode = isOpen && ReactDOM.createPortal(
    <div ref={menuRef} className="fixed z-[9999] w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 animate-scale-in" style={{ top: position.top, left: position.left }}>
      {actions.map((a) => (
        <button
          key={a.key}
          onClick={() => { if (!a.disabled) { onAction(a.key, draft); closeMenu(); } }}
          disabled={a.disabled}
          className={`w-full px-4 py-2.5 flex items-center gap-3 transition-all ${a.color} ${a.disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
        >
          {a.icon}
          <span className="font-medium">{a.label}</span>
        </button>
      ))}
    </div>,
    document.body
  );

  return (
    <div className="relative">
      <button ref={buttonRef} onClick={handleToggle} className="px-4 py-2 bg-gradient-to-r from-slate-600 to-slate-700 text-white rounded-lg hover:from-slate-700 hover:to-slate-800 transition-all shadow-md hover:shadow-lg flex items-center gap-2 font-medium">
        <span>Actions</span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
      </button>
      {menuNode}
    </div>
  );
};

/* ──────────────────────── Bulk Upload Modal ──────────────────────── */
const BulkUploadModal = ({ open, onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  if (!open) return null;

  const doUpload = async () => {
    if (!file) return;
    setUploading(true);
    try { await onUpload(file); setFile(null); onClose(true); } catch { /* parent handles */ } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-scale-in">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
            Bulk Upload Drafts
          </h3>
          <button onClick={() => onClose(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div onClick={() => fileRef.current?.click()} className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${file ? "border-blue-400 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50/50"}`}>
          <input ref={fileRef} type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0] || null)} className="hidden" />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <div className="text-left"><p className="font-semibold text-gray-900">{file.name}</p><p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p></div>
            </div>
          ) : (
            <>
              <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
              <p className="text-sm font-semibold text-gray-700 mb-1">Click to upload CSV file</p>
              <p className="text-xs text-gray-500">Only .csv files are accepted</p>
            </>
          )}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={() => onClose(false)} className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all">Cancel</button>
          <button onClick={doUpload} disabled={!file || uploading} className={`flex-1 px-4 py-2.5 rounded-xl font-semibold transition-all shadow-lg flex items-center justify-center gap-2 ${!file || uploading ? "bg-gray-300 cursor-not-allowed text-gray-500" : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"}`}>
            {uploading ? (<><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>Uploading...</>) : "Upload & Create Drafts"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────── Detail Drawer ──────────────────────── */
const DraftDetailDrawer = ({ draft, open, onClose }) => {
  if (!open || !draft) return null;

  const fields = [
    { label: "Draft Order ID", value: draft.draft_order_id },
    { label: "Status", value: <DraftStatusBadge status={draft.draft_status} /> },
    { label: "Source", value: <SourceBadge source={draft.order_source} /> },
    { label: "Channel", value: draft.order_source_channel_name || "\u2014" },
    { label: "External Ref", value: draft.order_source_reference_id || "\u2014" },
    { label: "Title", value: draft.draft_title || "\u2014" },
    { label: "Tags", value: draft.tags?.length ? draft.tags.join(", ") : "\u2014" },
    { label: "Notes", value: draft.internal_notes || "\u2014" },
    { label: "Carrier", value: draft.order_data?.shipment_details?.shipment_carrier_details?.carrier_partner_public_name || "\u2014" },
    { label: "Scheduled At", value: draft.scheduled_placement_datetime ? formatInIST(draft.scheduled_placement_datetime) : "\u2014" },
    { label: "Created By", value: draft.created_by || "\u2014" },
    { label: "Created At", value: draft.created_date_time ? formatInIST(draft.created_date_time) : "\u2014" },
    { label: "Updated By", value: draft.updated_by || "\u2014" },
    { label: "Updated At", value: draft.updated_date_time ? formatInIST(draft.updated_date_time) : "\u2014" },
  ];
  if (draft.placed_shipment_order_id) {
    fields.push({ label: "Placed Order ID", value: draft.placed_shipment_order_id }, { label: "Placed At", value: draft.placed_at ? formatInIST(draft.placed_at) : "\u2014" }, { label: "Placed By", value: draft.placed_by || "\u2014" });
  }
  if (draft.placement_error) {
    fields.push({ label: "Error Code", value: <span className="text-rose-700 font-semibold">{draft.placement_error.error_code}</span> }, { label: "Error", value: <span className="text-rose-600 font-medium">{parsePlacementErrorMessage(draft.placement_error.error_message)}</span> }, { label: "Technical Detail", value: <span className="text-rose-400 text-xs break-all">{draft.placement_error.error_message}</span> }, { label: "Retryable", value: draft.placement_error.retryable ? "Yes" : "No" });
  }
  if (draft.duplicated_from_draft_id) fields.push({ label: "Duplicated From Draft", value: draft.duplicated_from_draft_id });
  if (draft.duplicated_from_order_id) fields.push({ label: "Created From Order", value: draft.duplicated_from_order_id });

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex justify-end animate-fade-in" onClick={onClose}>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-white shadow-2xl h-full overflow-y-auto animate-slide-in-right" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-gradient-to-r from-slate-700 to-slate-800 text-white p-5 flex items-center justify-between">
          <div><h2 className="text-lg font-bold">Draft Order Details</h2><p className="text-sm text-slate-300 mt-0.5 font-mono">{draft.draft_order_id}</p></div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <div className="p-5 space-y-4">
          {fields.map((f, i) => (
            <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[120px] pt-0.5">{f.label}</span>
              <span className="text-sm text-gray-900 font-medium flex-1 break-all">{f.value}</span>
            </div>
          ))}
          {draft.order_data && (
            <div className="mt-4">
              <h4 className="text-sm font-bold text-gray-700 mb-2">Order Data Payload</h4>
              <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700 overflow-x-auto max-h-80 font-mono">{JSON.stringify(draft.order_data, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════════ */
export default function DraftOrdersPage() {
  const navigate = useNavigate();

  const defaultFrom = format(subMonths(new Date(), 1), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const [useDateFilter, setUseDateFilter] = useState(true);

  const [filters, setFilters] = useState({
    draftOrderId: "",
    searchKeyword: "",
    orderSource: "",
    draftTitle: "",
    tags: "",
    fromDate: defaultFrom,
    toDate: defaultTo,
  });

  const [selectedTab, setSelectedTab] = useState("DRAFT");
  const [statusCounts, setStatusCounts] = useState(Object.fromEntries(TABS.map(t => [t.key, 0])));

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalElements / limit));

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedRows, setSelectedRows] = useState(new Set());
  const headerCheckboxRef = useRef(null);

  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("compact");
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", onConfirm: null, confirmLabel: "Confirm", variant: "danger" });
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState({ open: false, draft: null });
  const [placingIds, setPlacingIds] = useState(new Set());
  const [bulkPlacing, setBulkPlacing] = useState(false);
  const [expandedErrorLogs, setExpandedErrorLogs] = useState({});
  const [retryingIds, setRetryingIds] = useState(new Set());

  // ─── Courier selection state ──
  const [carrierOptionsByKey, setCarrierOptionsByKey] = useState({}); // { draftOrderId: [{id,name,price,max_tat_in_days,min_tat_in_days}] }
  const [loadingCarrierKeys, setLoadingCarrierKeys] = useState(new Set());
  const [carrierSelectionByKey, setCarrierSelectionByKey] = useState({}); // { draftOrderId: carrier_partner_public_name }
  const [carrierDropdownOpenKey, setCarrierDropdownOpenKey] = useState(null);
  const [carrierDropdownPosition, setCarrierDropdownPosition] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const carrierTriggerRef = useRef(null);
  const carrierMenuRef = useRef(null);

  const showToast = useCallback((msg, type = "success") => setToast({ message: msg, type }), []);

  const pageSelectedCount = useMemo(() => drafts.filter(d => selectedRows.has(d.draft_order_id)).length, [drafts, selectedRows]);
  const allOnPageSelected = useMemo(() => drafts.length > 0 && drafts.every(d => selectedRows.has(d.draft_order_id)), [drafts, selectedRows]);
  const draftStatusSelectedCount = useMemo(() => drafts.filter(d => selectedRows.has(d.draft_order_id) && d.draft_status === "DRAFT").length, [drafts, selectedRows]);
  // Selected items that can be permanently deleted (anything except PLACED).
  const permanentDeletableSelectedCount = useMemo(
    () => drafts.filter(d => selectedRows.has(d.draft_order_id) && d.draft_status !== "PLACED").length,
    [drafts, selectedRows]
  );
  // Count of PLACED items in the current selection - used to warn the user during bulk delete.
  const placedSelectedCount = useMemo(
    () => drafts.filter(d => selectedRows.has(d.draft_order_id) && d.draft_status === "PLACED").length,
    [drafts, selectedRows]
  );

  // ── Pre-compute validation results for all DRAFT-status orders on this page ──
  const draftValidationResults = useMemo(() => {
    const results = {};
    for (const d of drafts) {
      if (d.draft_status === "DRAFT" || d.draft_status === "FAILED") {
        results[d.draft_order_id] = validateDraftOrderData(d.order_data);
      }
    }
    return results;
  }, [drafts]);

  const activeFiltersCount = useMemo(() => {
    let c = 0;
    if (filters.draftOrderId) c++;
    if (filters.searchKeyword) c++;
    if (filters.orderSource) c++;
    if (filters.draftTitle) c++;
    if (filters.tags) c++;
    return c;
  }, [filters]);

  const filtersRef = useRef(filters);
  const currentPageRef = useRef(currentPage);
  const limitRef = useRef(limit);
  const useDateFilterRef = useRef(useDateFilter);
  const selectedTabRef = useRef(selectedTab);
  filtersRef.current = filters;
  currentPageRef.current = currentPage;
  limitRef.current = limit;
  useDateFilterRef.current = useDateFilter;
  selectedTabRef.current = selectedTab;

  const fetchingRef = useRef(false);

  const fetchDrafts = useCallback(async (tabKey, overrides = {}) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const merchantId = localStorage.getItem("merchantId");
      if (!localStorage.getItem("authToken") || !merchantId) return;

      const f = filtersRef.current;
      const udf = useDateFilterRef.current;

      const baseBody = {
        ...(f.orderSource && { order_source: f.orderSource }),
        ...(f.searchKeyword && { search_keyword: f.searchKeyword }),
        ...(udf && {
          from_date: formatISO(toDate(parseISO(`${f.fromDate}T00:00:00+05:30`))),
          to_date: formatISO(toDate(parseISO(`${f.toDate}T23:59:59+05:30`))),
        }),
        page: (overrides.page ?? currentPageRef.current) - 1,
        size: limitRef.current,
        sort_by: "created_date_time",
        sort_direction: "DESC",
        ...overrides,
      };

      const headers = { merchantId };

      // Draft tab shows both DRAFT + FAILED orders via parallel fetch
      if (tabKey === "DRAFT") {
        const [draftRes, failedRes] = await Promise.all([
          API.post(ApiUrls.searchDraftOrders(), { ...baseBody, draft_status: "DRAFT" }, { headers }),
          API.post(ApiUrls.searchDraftOrders(), { ...baseBody, draft_status: "FAILED" }, { headers }),
        ]);
        const draftOrders = Array.isArray(draftRes.data?.draft_orders) ? draftRes.data.draft_orders : [];
        const failedOrders = Array.isArray(failedRes.data?.draft_orders) ? failedRes.data.draft_orders : [];
        const merged = [...draftOrders, ...failedOrders].sort(
          (a, b) => new Date(b.created_date_time) - new Date(a.created_date_time)
        );
        setDrafts(merged);
        setTotalElements((draftRes.data?.total_elements ?? 0) + (failedRes.data?.total_elements ?? 0));
      } else {
        const body = { ...baseBody, ...(tabKey !== "all" && { draft_status: tabKey }) };
        const res = await API.post(ApiUrls.searchDraftOrders(), body, { headers });
        setDrafts(Array.isArray(res.data.draft_orders) ? res.data.draft_orders : []);
        setTotalElements(res.data.total_elements ?? 0);
      }
    } catch (err) {
      setError(err?.message || "Failed to fetch draft orders.");
      showToast("Failed to fetch draft orders", "error");
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [showToast]);

  const fetchStatusCounts = useCallback(async (forceRefresh = false) => {
    try {
      if (!forceRefresh && _statusCountsCache && (Date.now() - _statusCountsCacheTime < STATUS_COUNTS_CACHE_TTL)) {
        setStatusCounts(_statusCountsCache);
        return;
      }

      const merchantId = localStorage.getItem("merchantId");
      if (!localStorage.getItem("authToken") || !merchantId) return;
      const statuses = ["DRAFT", "QUEUED", "PLACED", "FAILED", "DISCARDED"];
      const counts = { all: 0 };
      const f = filtersRef.current;
      const udf = useDateFilterRef.current;
      const dateFilters = udf ? {
        from_date: formatISO(toDate(parseISO(`${f.fromDate}T00:00:00+05:30`))),
        to_date: formatISO(toDate(parseISO(`${f.toDate}T23:59:59+05:30`))),
      } : {};

      const results = await Promise.allSettled(
        statuses.map(s => API.post(ApiUrls.searchDraftOrders(), { draft_status: s, page: 0, size: 1, ...dateFilters }, { headers: { merchantId } }))
      );
      results.forEach((r, i) => {
        const total = r.status === "fulfilled" ? (r.value.data?.total_elements ?? 0) : 0;
        counts[statuses[i]] = total;
        counts.all += total;
      });

      _statusCountsCache = counts;
      _statusCountsCacheTime = Date.now();

      setStatusCounts(counts);
    } catch {
    }
  }, []);

  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;
    fetchDrafts(selectedTabRef.current);
    fetchStatusCounts();
  }, [fetchDrafts, fetchStatusCounts]);

  const handleTabClick = (key) => {
    setSelectedTab(key);
    setCurrentPage(1);
    setSelectedRows(new Set());
    setExpandedErrorLogs({});
    fetchDrafts(key, { page: 0 });
  };

  const handleInputChange = (e) => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFilterClick = () => {
    setCurrentPage(1);
    setSelectedRows(new Set());
    fetchDrafts(selectedTab, { page: 0 });
    fetchStatusCounts(true);
  };

  const resetFilters = () => {
    setFilters({ draftOrderId: "", searchKeyword: "", orderSource: "", draftTitle: "", tags: "", fromDate: defaultFrom, toDate: defaultTo });
    setUseDateFilter(true);
    setCurrentPage(1);
    setSelectedRows(new Set());
    fetchDrafts(selectedTab, { page: 0 });
    fetchStatusCounts(true);
  };

  const toggleSelectOne = (id) => setSelectedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAllOnPage = (checked) => setSelectedRows(prev => { const n = new Set(prev); drafts.forEach(d => { checked ? n.add(d.draft_order_id) : n.delete(d.draft_order_id); }); return n; });

  const prevPage = () => {
    const newPage = Math.max(1, currentPage - 1);
    setCurrentPage(newPage);
    fetchDrafts(selectedTab, { page: newPage - 1 });
  };
  const nextPage = () => {
    if (drafts.length < limit) return;
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    fetchDrafts(selectedTab, { page: newPage - 1 });
  };
  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    fetchDrafts(selectedTab, { page: 0, size: newLimit });
  };

  const handlePlaceDraft = async (draftId) => {
    setPlacingIds(prev => new Set(prev).add(draftId));
    try {
      const merchantId = localStorage.getItem("merchantId");
      const res = await API.post(ApiUrls.placeDraftOrder(draftId), {}, { headers: { merchantId } });
      if (res.data?.draft_status === "PLACED") showToast(`Draft placed successfully! Order: ${res.data.placed_shipment_order_id}`);
      else if (res.data?.draft_status === "FAILED") showToast(`Placement failed: ${parsePlacementErrorMessage(res.data.placement_error?.error_message)}`, "error");
      fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) { showToast(err?.message || "Failed to place draft order", "error"); }
    finally { setPlacingIds(prev => { const n = new Set(prev); n.delete(draftId); return n; }); }
  };

  const handleDiscardDraft = async (draftId) => {
    try {
      const merchantId = localStorage.getItem("merchantId");
      await API.delete(ApiUrls.discardDraftOrder(draftId), { headers: { merchantId } });
      showToast("Draft order discarded successfully");
      fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) { showToast(err?.message || "Failed to discard", "error"); }
  };

  const handleDuplicateDraft = async (draft) => {
    try {
      const merchantId = localStorage.getItem("merchantId");
      const res = await API.post(ApiUrls.duplicateDraftOrder(draft.draft_order_id), {}, { headers: { merchantId } });
      const newDraftId = res.data?.draft_order_id;

      // Increment order_reference_number on the duplicated draft to avoid duplicates
      const od = draft.order_data || {};
      const orderRef = od.shipment_details?.order_details?.order_reference_number;
      if (newDraftId && orderRef) {
        const suffixMatch = orderRef.match(/_(\d+)$/);
        const newRef = suffixMatch
          ? orderRef.replace(/_(\d+)$/, `_${parseInt(suffixMatch[1], 10) + 1}`)
          : `${orderRef}_1`;

        const updatedOrderData = JSON.parse(JSON.stringify(od));
        updatedOrderData.shipment_details = updatedOrderData.shipment_details || {};
        updatedOrderData.shipment_details.order_details = updatedOrderData.shipment_details.order_details || {};
        updatedOrderData.shipment_details.order_details.order_reference_number = newRef;

        await API.put(ApiUrls.updateDraftOrder(newDraftId), { order_data: updatedOrderData }, { headers: { merchantId } });
      }

      showToast(`Draft duplicated! New ID: ${newDraftId}`);
      fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) { showToast(err?.message || "Failed to duplicate", "error"); }
  };

  const handleBulkPlace = async () => {
    const eligibleDrafts = Array.from(selectedRows)
      .map(id => drafts.find(d => d.draft_order_id === id && d.draft_status === "DRAFT"))
      .filter(Boolean);
    if (!eligibleDrafts.length) { showToast("No eligible drafts selected (must be DRAFT status)", "error"); return; }

    // Validate every eligible draft
    const validIds = [];
    const invalidDrafts = [];
    for (const draft of eligibleDrafts) {
      const errors = validateDraftOrderData(draft.order_data);
      if (errors.length > 0) invalidDrafts.push({ id: draft.draft_order_id, title: draft.draft_title, errors });
      else validIds.push(draft.draft_order_id);
    }

    if (validIds.length === 0) {
      showToast(`All ${invalidDrafts.length} selected draft(s) have missing mandatory fields. Please complete them before placing.`, "error");
      return;
    }
    if (invalidDrafts.length > 0) {
      showToast(`${invalidDrafts.length} draft(s) skipped (missing fields). Placing ${validIds.length} valid draft(s).`, "info");
    }

    setBulkPlacing(true);
    try {
      const merchantId = localStorage.getItem("merchantId");
      const res = await API.post(ApiUrls.bulkPlaceDraftOrders(), { draft_order_ids: validIds }, { headers: { merchantId } });
      const d = res.data;
      if (d.status === "COMPLETED") showToast(`${d.total_queued} draft(s) placed successfully!`);
      else if (d.status === "ACCEPTED") showToast(`${d.total_queued} draft(s) queued. ${d.total_rejected} rejected.`, "info");
      setSelectedRows(new Set()); fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) { showToast(err?.message || "Bulk placement failed", "error"); }
    finally { setBulkPlacing(false); }
  };

  const handleBulkDiscard = async () => {
    const ids = Array.from(selectedRows).filter(id => drafts.find(d => d.draft_order_id === id && d.draft_status === "DRAFT"));
    if (!ids.length) { showToast("No eligible drafts selected (must be DRAFT status)", "error"); return; }
    try {
      const merchantId = localStorage.getItem("merchantId");
      await API.delete(ApiUrls.bulkDeleteDraftOrders(), { data: { draft_order_ids: ids }, headers: { merchantId } });
      showToast(`${ids.length} draft(s) discarded`);
      setSelectedRows(new Set()); fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) { showToast(err?.message || "Bulk discard failed", "error"); }
  };

  /**
   * Permanently delete a draft order.
   * The backend only allows permanent deletion when the draft is in DISCARDED status.
   * For any other status (DRAFT / QUEUED / FAILED) we first discard it (soft-delete),
   * then issue the permanent delete. PLACED is never permitted.
   */
  const handlePermanentDeleteDraft = async (draftId, currentStatus) => {
    if (currentStatus === "PLACED") {
      showToast("Placed orders cannot be permanently deleted.", "error");
      return;
    }
    try {
      const merchantId = localStorage.getItem("merchantId");
      // Step 1: Move to DISCARDED if not already
      if (currentStatus !== "DISCARDED") {
        await API.delete(ApiUrls.discardDraftOrder(draftId), { headers: { merchantId } });
      }
      // Step 2: Permanently delete
      await API.delete(ApiUrls.permanentDeleteDraftOrder(draftId), { headers: { merchantId } });
      showToast("Draft order permanently deleted");
      fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to permanently delete draft", "error");
    }
  };

  /**
   * Bulk permanently delete selected drafts.
   * - Excludes any PLACED items (placed shipments can never be deleted).
   * - Each non-DISCARDED draft is discarded first, then permanently deleted.
   * - Reports a per-item success / failure summary.
   */
  const handleBulkPermanentDelete = async () => {
    const selectedDrafts = Array.from(selectedRows)
      .map(id => drafts.find(d => d.draft_order_id === id))
      .filter(Boolean);
    const placedCount = selectedDrafts.filter(d => d.draft_status === "PLACED").length;
    const eligible = selectedDrafts.filter(d => d.draft_status !== "PLACED");

    if (!eligible.length) {
      showToast("No eligible drafts selected. Placed orders cannot be permanently deleted.", "error");
      return;
    }

    const merchantId = localStorage.getItem("merchantId");
    let success = 0;
    const failures = [];

    for (const d of eligible) {
      try {
        if (d.draft_status !== "DISCARDED") {
          await API.delete(ApiUrls.discardDraftOrder(d.draft_order_id), { headers: { merchantId } });
        }
        await API.delete(ApiUrls.permanentDeleteDraftOrder(d.draft_order_id), { headers: { merchantId } });
        success++;
      } catch (err) {
        failures.push({ id: d.draft_order_id, reason: err?.response?.data?.message || err?.message || "Unknown error" });
      }
    }

    setSelectedRows(new Set());
    fetchDrafts(selectedTab);
    fetchStatusCounts(true);

    if (failures.length === 0 && placedCount === 0) {
      showToast(`${success} draft(s) permanently deleted`);
    } else if (failures.length === 0 && placedCount > 0) {
      showToast(`${success} draft(s) permanently deleted (${placedCount} placed order(s) skipped)`, "info");
    } else {
      showToast(`${success} deleted, ${failures.length} failed${placedCount > 0 ? `, ${placedCount} placed skipped` : ""}`, "error");
    }
  };

  const handleBulkUpload = async (file) => {
    const merchantId = localStorage.getItem("merchantId");
    const fd = new FormData(); fd.append("file", file);
    const res = await API.post(ApiUrls.bulkUploadDraftOrders(), fd, { headers: { merchantId, "Content-Type": "multipart/form-data" } });
    showToast(`${res.data?.total_drafts_created ?? 0} draft(s) created from CSV`);
    fetchDrafts(selectedTab); fetchStatusCounts(true);
  };

  const handleRetryPlace = async (draft) => {
    const draftId = draft.draft_order_id;
    setRetryingIds(prev => new Set(prev).add(draftId));
    try {
      const merchantId = localStorage.getItem("merchantId");
      const res = await API.post(ApiUrls.placeDraftOrder(draftId), {}, { headers: { merchantId } });
      if (res.data?.draft_status === "PLACED") showToast(`Draft placed successfully! Order: ${res.data.placed_shipment_order_id}`);
      else if (res.data?.draft_status === "FAILED") showToast(`Placement failed: ${parsePlacementErrorMessage(res.data.placement_error?.error_message)}`, "error");
      else showToast("Retry placement submitted successfully");
      fetchDrafts(selectedTab); fetchStatusCounts(true);
    } catch (err) {
      showToast(err?.message || "Failed to retry placement", "error");
    } finally {
      setRetryingIds(prev => { const n = new Set(prev); n.delete(draftId); return n; });
    }
  };

  /* ──────────────────── Courier selection - dropdown positioning ──────────────────── */
  const DROPDOWN_MAX_HEIGHT = 280;
  const computeCarrierDropdownPosition = useCallback(() => {
    if (!carrierTriggerRef.current) return;
    const rect = carrierTriggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > DROPDOWN_MAX_HEIGHT;
    const width = Math.max(rect.width, 280);
    // Clamp left so dropdown doesn't overflow viewport
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    setCarrierDropdownPosition({
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      left: Math.max(8, left),
      width,
      openUp,
    });
  }, []);

  useLayoutEffect(() => {
    if (carrierDropdownOpenKey == null) return;
    requestAnimationFrame(computeCarrierDropdownPosition);
    const handleUpdate = () => requestAnimationFrame(computeCarrierDropdownPosition);
    window.addEventListener("resize", handleUpdate);
    return () => { window.removeEventListener("resize", handleUpdate); };
  }, [carrierDropdownOpenKey, computeCarrierDropdownPosition]);

  // Close courier dropdown on click outside OR on external scroll (not inside the dropdown)
  useEffect(() => {
    if (carrierDropdownOpenKey == null) return;
    const onMouseDown = (e) => {
      const inRoot = e.target.closest(`[data-carrier-dropdown-root="${carrierDropdownOpenKey}"]`);
      const inMenu = carrierMenuRef.current && carrierMenuRef.current.contains(e.target);
      if (!inRoot && !inMenu) setCarrierDropdownOpenKey(null);
    };
    const onScroll = (e) => {
      if (carrierMenuRef.current && carrierMenuRef.current.contains(e.target)) return;
      setCarrierDropdownOpenKey(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("scroll", onScroll, true);
    return () => { document.removeEventListener("mousedown", onMouseDown); document.removeEventListener("scroll", onScroll, true); };
  }, [carrierDropdownOpenKey]);

  /* ──────────────────── Courier selection - fetch carrier options ──────────────────── */
  const performCourierFetchForDraft = async (draft) => {
    const draftId = draft.draft_order_id;
    const od = draft.order_data || {};
    const authToken = localStorage.getItem("authToken");

    if (!authToken) { showToast("Auth token missing. Please sign in again.", "error"); return; }

    let allocatedCarriers = [];
    try { allocatedCarriers = JSON.parse(localStorage.getItem("allocatedCarriers") || "[]") || []; }
    catch { showToast("Carrier configuration is invalid.", "error"); return; }

    if (!Array.isArray(allocatedCarriers) || allocatedCarriers.length === 0) {
      showToast("No allocated carriers found. Please configure carriers first.", "error"); return;
    }

    const carrierIdsToEvaluate = allocatedCarriers.map(c => c.carrier_partner_id);
    const pickup = od.pickup_details || {};
    const drop = od.drop_details || {};
    const sd = od.shipment_details || {};
    const orderDet = sd.order_details || {};
    const firstPkg = (sd.shipment_packages || [])[0] || {};

    const pickupPincode = pickup.pickup_postal_code;
    const dropPincode = drop.drop_postal_code;

    if (!pickupPincode || !dropPincode) {
      showToast("Pickup or delivery pincode missing in draft order data.", "error"); return;
    }

    const correlationId = uuidv4();
    const payload = [{
      correlation_id: correlationId,
      carrier_options_calculation_context: {
        shipment_type: od.shipment_order_type || "FORWARD",
        payment_mode: orderDet.order_type || null,
        invoice_value: orderDet.order_invoice_details?.invoice_value || 0,
        rvp_with_quality_check: false,
        weight_in_grams: Number(firstPkg.weight?.value || 0),
        length_in_cm: Number(firstPkg.dimensions?.length || 0),
        breadth_in_cm: Number(firstPkg.dimensions?.breadth || 0),
        height_in_cm: Number(firstPkg.dimensions?.height || 0),
        pickup_pincode: pickupPincode,
        drop_pincode: dropPincode,
      },
      carrier_partner_ids_to_evaluate: carrierIdsToEvaluate,
    }];

    setLoadingCarrierKeys(prev => { const n = new Set(prev); n.add(draftId); return n; });

    try {
      const res = await axios.post(
        ApiUrls.retrieveCarrierOptionsInBatch(),
        payload,
        { headers: { Authorization: authToken, "Content-Type": "application/json" } }
      );

      for (const result of res.data || []) {
        const map = result.carrier_options_calculation_result_by_carrier_partner_id || {};
        // Deduplicate by carrier_partner_public_name: keep first occurrence only
        const seenNames = new Set();
        const serviceableOptions = Object.entries(map)
          .filter(([_, val]) => val && val.serviceable === true)
          .map(([id, val]) => ({
            id,
            name: val.carrier_partner_public_name,
            price: val.total_amount_to_charge_after_tax,
            max_tat_in_days: val.max_tat_in_days,
            min_tat_in_days: val.min_tat_in_days,
          }))
          .filter((opt) => {
            if (seenNames.has(opt.name)) return false;
            seenNames.add(opt.name);
            return true;
          });

        setCarrierOptionsByKey(prev => ({ ...prev, [draftId]: serviceableOptions }));

        if (serviceableOptions.length > 0) {
          const cheapest = serviceableOptions.reduce((min, o) => (o.price < min.price ? o : min), serviceableOptions[0]);
          setCarrierSelectionByKey(prev => ({ ...prev, [draftId]: cheapest.name }));
        } else {
          showToast("No serviceable courier options found for this draft.", "error");
        }
      }
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to fetch courier options.", "error");
    } finally {
      setLoadingCarrierKeys(prev => { const n = new Set(prev); n.delete(draftId); return n; });
    }
  };

  /* ──────────────────── Courier selection - update draft with chosen carrier ──────────────────── */
  const handleCourierSelect = async (draftId, carrierPublicName) => {
    setCarrierSelectionByKey(prev => ({ ...prev, [draftId]: carrierPublicName }));
    setCarrierDropdownOpenKey(null);

    // Update draft order_data with shipment_carrier_details.carrier_partner_public_name
    const draft = drafts.find(d => d.draft_order_id === draftId);
    const currentOrderData = draft?.order_data || {};
    const updatedShipmentDetails = { ...(currentOrderData.shipment_details || {}) };
    if (carrierPublicName) {
      updatedShipmentDetails.shipment_carrier_details = {
        carrier_partner_public_name: carrierPublicName,
      };
    } else {
      delete updatedShipmentDetails.shipment_carrier_details;
    }
    const updatedOrderData = { ...currentOrderData, shipment_details: updatedShipmentDetails };

    try {
      const merchantId = localStorage.getItem("merchantId");
      await API.put(ApiUrls.updateDraftOrder(draftId), { order_data: updatedOrderData }, { headers: { merchantId } });
      showToast(carrierPublicName ? "Courier preference saved" : "Courier preference cleared");
      // Update local draft data to reflect the change
      setDrafts(prev => prev.map(d => d.draft_order_id === draftId ? { ...d, order_data: updatedOrderData } : d));
    } catch (err) {
      showToast(err?.message || "Failed to update courier preference", "error");
    }
  };

  /**
   * Flatten draft order_data (nested pickup_details/drop_details/shipment_details)
   * into the flat structure that CreateForwardOrder/CreateReverseOrder cloneFrom expects.
   */
  const normalizeDraftOrderData = (od) => {
    if (!od) return {};
    const pickup = od.pickup_details || {};
    const drop = od.drop_details || {};
    const sd = od.shipment_details || {};
    const orderDet = sd.order_details || {};
    const pkgs = sd.shipment_packages || [];
    const firstPkg = pkgs[0] || {};

    return {
      shipment_order_type: od.shipment_order_type,
      // Flat pickup fields
      pickup_poc: pickup.pickup_poc,
      pickup_name: pickup.pickup_poc,
      pickup_organisation: pickup.pickup_organisation,
      pickup_address: pickup.pickup_address,
      pickup_district: pickup.pickup_district,
      pickup_city: pickup.pickup_city,
      pickup_state: pickup.pickup_state,
      pickup_postal_code: pickup.pickup_postal_code,
      pickup_pincode: pickup.pickup_postal_code,
      pickup_landmark: pickup.pickup_landmark,
      pickup_poc_email: pickup.pickup_poc_email,
      pickup_poc_contact_number: pickup.pickup_poc_contact_number,
      pickup_country_code: pickup.pickup_country_code,
      pickup_address_type: pickup.pickup_address_type,
      gst_number: pickup.gst_number,
      // Flat drop fields
      drop_name: drop.drop_poc,
      drop_poc: drop.drop_poc,
      drop_organisation: drop.drop_organisation,
      drop_address: drop.drop_address,
      drop_city: drop.drop_city,
      drop_state: drop.drop_state,
      drop_postal_code: drop.drop_postal_code,
      drop_pincode: drop.drop_postal_code,
      drop_poc_email: drop.drop_poc_email,
      drop_poc_contact_number: drop.drop_poc_contact_number,
      drop_country_code: drop.drop_country_code,
      drop_landmark: drop.drop_landmark,
      // Order details
      order_type: orderDet.order_type,
      order_reference_number: orderDet.order_reference_number,
      invoice_number: orderDet.order_invoice_details?.invoice_number,
      // Payment
      payment_details: {
        payment_mode: orderDet.order_type,
        total_amount: orderDet.cod_amount_details?.cod_amount || orderDet.order_invoice_details?.invoice_value,
      },
      // Packages - use shipment_packages at top level like search-orders API
      shipment_packages: pkgs,
      // Package dimensions for the first package
      shipment_package_dimensions: firstPkg.dimensions || {},
      dead_weight_in_grams: firstPkg.weight?.value,
      product_weight: firstPkg.weight,
    };
  };

  const handleActionClick = (action, draft) => {
    switch (action) {
      case "view":
        setDetailDrawer({ open: true, draft });
        break;
      case "place": {
        const valErrors = validateDraftOrderData(draft.order_data);
        if (valErrors.length > 0) {
          const grouped = valErrors.reduce((acc, e) => { (acc[e.section] = acc[e.section] || []).push(e.field); return acc; }, {});
          setConfirmModal({
            open: true,
            title: "Cannot Place - Missing Mandatory Fields",
            message: (
              <div className="text-left">
                <p className="text-gray-600 mb-3">
                  Draft "<strong className="text-gray-800">{draft.draft_title || draft.draft_order_id}</strong>" is missing
                  <span className="font-bold text-rose-600"> {valErrors.length} mandatory field{valErrors.length > 1 ? "s" : ""}</span>:
                </p>
                <div className="space-y-2 mb-4 max-h-56 overflow-y-auto bg-rose-50 rounded-lg p-3 border border-rose-200">
                  {Object.entries(grouped).map(([section, fields]) => (
                    <div key={section}>
                      <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">{section}</span>
                      <ul className="mt-0.5 space-y-0.5">
                        {fields.map(f => (
                          <li key={f} className="text-sm text-rose-700 flex items-center gap-1.5">
                            <svg className="w-3 h-3 text-rose-400 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500">Please edit the draft to complete all required fields before placing.</p>
              </div>
            ),
            onConfirm: () => {
              const od = draft.order_data || {};
              const normalized = normalizeDraftOrderData(od);
              const isReverse = (od.shipment_order_type || "").toUpperCase() === "REVERSE";
              navigate(isReverse ? "/create-reverse-order" : "/create-forward-order", { state: { cloneFrom: normalized, draftOrderId: draft.draft_order_id } });
            },
            confirmLabel: "Edit Draft",
            variant: "danger",
          });
        } else {
          setConfirmModal({ open: true, title: "Place Draft Order", message: `Place "${draft.draft_title || draft.draft_order_id}"? This will create a live shipment order.`, onConfirm: () => handlePlaceDraft(draft.draft_order_id), confirmLabel: "Place Order", variant: "info" });
        }
        break;
      }
      case "retry":
        setConfirmModal({ open: true, title: "Retry Placement", message: `Retry placing "${draft.draft_title || draft.draft_order_id}"? This will attempt to create a live shipment order again.`, onConfirm: () => handleRetryPlace(draft), confirmLabel: "Retry", variant: "info" });
        break;
      case "edit": {
        const od = draft.order_data || {};
        const normalized = normalizeDraftOrderData(od);
        // For failed drafts, append _1, _2, etc. to order_reference_number
        if (draft.draft_status === "FAILED" && normalized.order_reference_number) {
          const ref = normalized.order_reference_number;
          const suffixMatch = ref.match(/_(\d+)$/);
          if (suffixMatch) {
            normalized.order_reference_number = ref.replace(/_(\d+)$/, `_${parseInt(suffixMatch[1], 10) + 1}`);
          } else {
            normalized.order_reference_number = `${ref}_1`;
          }
        }
        const isReverse = (od.shipment_order_type || "").toUpperCase() === "REVERSE";
        const route = isReverse ? "/create-reverse-order" : "/create-forward-order";
        navigate(route, { state: { cloneFrom: normalized, draftOrderId: draft.draft_order_id } });
        break;
      }
      case "duplicate":
        handleDuplicateDraft(draft);
        break;
      case "viewShipment":
        if (draft.placed_shipment_order_id) {
          const ref = encodeURIComponent(draft.placed_shipment_order_id);
          window.open(`${window.location.origin}/shipment-details/${ref}`, "_blank", "noopener,noreferrer");
        }
        break;
      case "discard":
        setConfirmModal({ open: true, title: "Discard Draft", message: `Discard "${draft.draft_title || draft.draft_order_id}"? This cannot be undone.`, onConfirm: () => handleDiscardDraft(draft.draft_order_id), confirmLabel: "Discard", variant: "danger" });
        break;
      case "permanentDelete": {
        if (draft.draft_status === "PLACED") {
          showToast("Placed orders cannot be permanently deleted.", "error");
          break;
        }
        const willDiscardFirst = draft.draft_status !== "DISCARDED";
        setConfirmModal({
          open: true,
          title: "Permanently Delete Draft",
          message: (
            <div className="text-left">
              <p className="text-gray-700 mb-3">
                You are about to <strong className="text-red-700">permanently delete</strong> draft
                "<strong className="text-gray-900">{draft.draft_title || draft.draft_order_id}</strong>".
              </p>
              {willDiscardFirst && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-sm text-amber-800">
                  <span className="font-semibold">Note:</span> This draft is currently <strong>{draft.draft_status}</strong>.
                  It will first be discarded and then permanently removed.
                </div>
              )}
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                <span className="font-semibold">This action is irreversible.</span> The draft and its data cannot be recovered.
              </div>
            </div>
          ),
          onConfirm: () => handlePermanentDeleteDraft(draft.draft_order_id, draft.draft_status),
          confirmLabel: "Delete Permanently",
          variant: "danger",
        });
        break;
      }
      default: break;
    }
  };

  /* ══════════════════════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-50 pb-8 overflow-x-hidden">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <ConfirmModal open={confirmModal.open} title={confirmModal.title} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onCancel={() => setConfirmModal({ open: false, title: "", message: "", onConfirm: null, confirmLabel: "Confirm", variant: "danger" })} confirmLabel={confirmModal.confirmLabel} variant={confirmModal.variant} />
      <BulkUploadModal open={bulkUploadOpen} onClose={() => setBulkUploadOpen(false)} onUpload={handleBulkUpload} />
      <DraftDetailDrawer draft={detailDrawer.draft} open={detailDrawer.open} onClose={() => setDetailDrawer({ open: false, draft: null })} />

      <div className="w-full max-w-full px-2 sm:px-3 lg:px-2 py-2 overflow-x-hidden">
        {/* Main Container */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200 w-full max-w-full">

          {/* ===== Tabs + Bulk Upload + Refresh ===== */}
          <div className="sticky top-0 z-20 bg-gradient-to-r from-slate-100 to-gray-100 border-b border-gray-300 shadow-sm">
            {/* Mobile */}
            <div className="md:hidden p-1">
              <div className="grid grid-cols-2 gap-1">
                {TABS.map((tab) => (
                  <button key={tab.key} onClick={() => handleTabClick(tab.key)} className={`relative flex flex-col items-center justify-center gap-1 px-2 py-1.5 rounded-lg font-medium transition-all duration-200 ${selectedTab === tab.key ? "bg-white shadow-md text-gray-900 ring-1 ring-slate-400/30" : "text-gray-600 bg-white/30 active:bg-white/60"}`}>
                    <div className="flex items-center gap-1">
                      <tab.Icon className={`w-3.5 h-3.5 transition-colors flex-shrink-0 ${selectedTab === tab.key ? tab.color : "text-gray-400"}`} />
                      <span className="text-[10px] font-semibold truncate max-w-[90px]">{tab.label}</span>
                    </div>
                    <span className={`${tab.badgeColor} text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center shadow-sm`}>{tab.key === "DRAFT" ? ((statusCounts["DRAFT"] ?? 0) + (statusCounts["FAILED"] ?? 0)) : (statusCounts[tab.key] ?? 0)}</span>
                    {selectedTab === tab.key && <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-slate-500 to-slate-600 rounded-t-full" />}
                  </button>
                ))}
                {/* Mobile: Bulk Upload + Refresh */}
                <div className="col-span-2 flex items-center justify-end gap-1 pt-0.5">
                  <button onClick={() => setBulkUploadOpen(true)} className="px-2.5 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg text-[10px] font-semibold transition-all shadow-sm flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                    Upload CSV
                  </button>
                  <button onClick={() => { fetchDrafts(selectedTab); fetchStatusCounts(true); }} className="p-1.5 bg-white/60 hover:bg-white rounded-lg transition-all text-slate-600" title="Refresh">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  </button>
                </div>
              </div>
            </div>
            {/* Desktop */}
            <div className="hidden md:block relative">
              <div className="overflow-x-auto scrollbar-hide scroll-smooth">
                <div className="flex p-1 gap-1 min-w-min items-center">
                  {TABS.map((tab) => (
                    <button key={tab.key} onClick={() => handleTabClick(tab.key)} className={`group relative flex items-center gap-1 px-2 py-1 rounded-md font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 ${selectedTab === tab.key ? "bg-white shadow-md text-gray-900 ring-1 ring-slate-400/30" : "text-gray-600 hover:bg-white/50 hover:text-gray-900"}`}>
                      <tab.Icon className={`w-3.5 h-3.5 transition-colors flex-shrink-0 ${selectedTab === tab.key ? tab.color : "text-gray-400 group-hover:text-gray-600"}`} />
                      <span className="text-xs font-semibold">{tab.label}</span>
                      <span className={`${tab.badgeColor} text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center shadow-sm`}>{tab.key === "DRAFT" ? ((statusCounts["DRAFT"] ?? 0) + (statusCounts["FAILED"] ?? 0)) : (statusCounts[tab.key] ?? 0)}</span>
                      {selectedTab === tab.key && <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2/3 h-0.5 bg-gradient-to-r from-slate-500 to-slate-600 rounded-t-full" />}
                    </button>
                  ))}
                  {/* Spacer pushes actions to right */}
                  <div className="flex-1" />
                  <button onClick={() => setBulkUploadOpen(true)} className="px-2.5 py-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-md text-xs font-semibold transition-all shadow-sm hover:shadow-md flex items-center gap-1.5 flex-shrink-0">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                    Bulk Upload CSV
                  </button>
                  <button onClick={() => { fetchDrafts(selectedTab); fetchStatusCounts(true); }} className="p-1.5 bg-white/60 hover:bg-white rounded-md transition-all text-slate-600 flex-shrink-0" title="Refresh">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Filters ===== */}
          <div className="top-[73px] z-20 border-b border-gray-300 bg-white">
            <div className="p-1.5 md:p-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1.5">
              <button onClick={() => setShowFilters(!showFilters)} className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all font-medium text-slate-700">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                <span className="font-semibold text-sm">Filters</span>
                {activeFiltersCount > 0 && <span className="bg-slate-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{activeFiltersCount}</span>}
                <svg className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${showFilters ? "rotate-180" : ""}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>

              <div className="w-full sm:w-auto flex items-center gap-1 flex-wrap">
                {/* Bulk Place */}
                <button
                  onClick={() => {
                    // Pre-validate selected drafts and build an informative confirmation message
                    const eligible = Array.from(selectedRows)
                      .map(id => drafts.find(d => d.draft_order_id === id && d.draft_status === "DRAFT"))
                      .filter(Boolean);
                    const invalid = eligible.filter(d => (draftValidationResults[d.draft_order_id] || []).length > 0);
                    const valid = eligible.length - invalid.length;
                    const msgParts = [];
                    if (valid > 0) msgParts.push(`${valid} draft(s) are ready and will be placed as live shipment orders.`);
                    if (invalid.length > 0) msgParts.push(`${invalid.length} draft(s) have missing mandatory fields and will be skipped.`);
                    if (valid === 0) { showToast(`All ${invalid.length} selected draft(s) have missing mandatory fields. Please complete them first.`, "error"); return; }
                    setConfirmModal({ open: true, title: "Bulk Place Drafts", message: msgParts.join(" "), onConfirm: handleBulkPlace, confirmLabel: `Place ${valid} Draft${valid > 1 ? "s" : ""}`, variant: "info" });
                  }}
                  disabled={draftStatusSelectedCount === 0 || bulkPlacing}
                  className={`px-2 py-1 rounded-lg text-white font-semibold text-xs transition-all shadow-md flex items-center justify-center gap-1 ${draftStatusSelectedCount === 0 || bulkPlacing ? "bg-gray-300 cursor-not-allowed" : "bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 hover:shadow-lg"}`}
                >
                  {bulkPlacing ? <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg> : <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                  <span className="hidden lg:inline">Place</span>
                  {draftStatusSelectedCount > 0 && <span className="bg-white text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{draftStatusSelectedCount}</span>}
                </button>
                {/* Bulk Discard */}
                <button
                  onClick={() => setConfirmModal({ open: true, title: "Bulk Discard", message: `Discard ${draftStatusSelectedCount} selected draft(s)? This cannot be undone.`, onConfirm: handleBulkDiscard, confirmLabel: "Discard All", variant: "danger" })}
                  disabled={draftStatusSelectedCount === 0}
                  className={`px-2 py-1 rounded-lg text-white font-semibold text-xs transition-all shadow-md flex items-center justify-center gap-1 ${draftStatusSelectedCount === 0 ? "bg-gray-300 cursor-not-allowed" : "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 hover:shadow-lg"}`}
                >
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  <span className="hidden lg:inline">Discard</span>
                  {draftStatusSelectedCount > 0 && <span className="bg-white text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{draftStatusSelectedCount}</span>}
                </button>
                {/* Bulk Permanently Delete - hidden on the PLACED tab (placed orders can't be deleted) */}
                {selectedTab !== "PLACED" && (
                  <button
                    onClick={() => {
                      if (permanentDeletableSelectedCount === 0) {
                        showToast(placedSelectedCount > 0 ? "Placed orders cannot be permanently deleted." : "No drafts selected.", "error");
                        return;
                      }
                      setConfirmModal({
                        open: true,
                        title: "Permanently Delete Drafts",
                        message: (
                          <div className="text-left">
                            <p className="text-gray-700 mb-3">
                              You are about to <strong className="text-red-700">permanently delete</strong> {permanentDeletableSelectedCount} draft{permanentDeletableSelectedCount > 1 ? "s" : ""}.
                            </p>
                            {placedSelectedCount > 0 && (
                              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-sm text-amber-800">
                                <span className="font-semibold">Note:</span> {placedSelectedCount} placed order{placedSelectedCount > 1 ? "s" : ""} in your selection will be skipped - placed shipments cannot be deleted.
                              </div>
                            )}
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                              <span className="font-semibold">This action is irreversible.</span> Non-discarded drafts will be discarded and then permanently removed. Data cannot be recovered.
                            </div>
                          </div>
                        ),
                        onConfirm: handleBulkPermanentDelete,
                        confirmLabel: `Delete ${permanentDeletableSelectedCount} Permanently`,
                        variant: "danger",
                      });
                    }}
                    disabled={permanentDeletableSelectedCount === 0}
                    className={`px-2 py-1 rounded-lg text-white font-semibold text-xs transition-all shadow-md flex items-center justify-center gap-1 ${permanentDeletableSelectedCount === 0 ? "bg-gray-300 cursor-not-allowed" : "bg-gradient-to-r from-red-700 to-red-800 hover:from-red-800 hover:to-red-900 hover:shadow-lg"}`}
                    title="Permanently delete selected drafts (excludes Placed orders)"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.5 11.5l5 5m0-5l-5 5" />
                    </svg>
                    <span className="hidden lg:inline">Delete Forever</span>
                    {permanentDeletableSelectedCount > 0 && <span className="bg-white text-red-800 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{permanentDeletableSelectedCount}</span>}
                  </button>
                )}
                {/* View toggle */}
                <button onClick={() => setViewMode(viewMode === "comfortable" ? "compact" : "comfortable")} className="flex-1 sm:flex-none px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all text-slate-700 text-xs font-medium flex items-center justify-center gap-1" title={`Switch to ${viewMode === "comfortable" ? "compact" : "comfortable"} view`}>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {viewMode === "comfortable" ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />}
                  </svg>
                  <span className="hidden sm:inline text-xs">{viewMode === "comfortable" ? "Compact" : "Comfortable"}</span>
                </button>
              </div>
            </div>

            {showFilters && (
              <form
                onSubmit={(e) => { e.preventDefault(); handleFilterClick(); }}
                className="p-2 md:p-3 bg-gradient-to-br from-slate-50 to-gray-50 border-t border-gray-200 animate-slide-down"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-2.5 mb-3">
                  {/* Draft Order ID */}
                  <div className="group w-full">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>
                      <span>Draft Order ID</span>
                    </label>
                    <input type="text" name="draftOrderId" value={filters.draftOrderId} onChange={handleInputChange} placeholder="DRF-xxxx..." className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm" />
                  </div>

                  {/* Draft Title */}
                  <div className="group w-full">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      <span>Draft Title</span>
                    </label>
                    <input type="text" name="draftTitle" value={filters.draftTitle} onChange={handleInputChange} placeholder="e.g. Bulk Diwali Sale" className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm" />
                  </div>

                  {/* Search Keyword */}
                  <div className="group w-full">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                      <span>Keyword Search</span>
                    </label>
                    <input type="text" name="searchKeyword" value={filters.searchKeyword} onChange={handleInputChange} placeholder="Search by notes, customer, etc." className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm" />
                  </div>

                  {/* Order Source */}
                  <div className="group w-full">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" /></svg>
                      <span>Order Source</span>
                    </label>
                    <select name="orderSource" value={filters.orderSource} onChange={handleInputChange} className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm">
                      <option value="">All Sources</option>
                      {Object.values(DraftOrderSourceEnum).map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                  </div>

                  {/* Tags */}
                  <div className="group w-full">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                      <span>Tags</span>
                    </label>
                    <input type="text" name="tags" value={filters.tags} onChange={handleInputChange} placeholder="e.g. priority, diwali-sale" className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm" />
                  </div>

                  {/* Created Date Range */}
                  <div className="sm:col-span-1 w-full">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        <span>Created Date</span>
                      </label>
                      {useDateFilter && <button type="button" onClick={() => setUseDateFilter(false)} className="text-[10px] text-red-600 hover:text-red-700 font-medium underline">Remove</button>}
                    </div>
                    {useDateFilter ? (
                      <div className="[&_.MuiPaper-root]:!p-1 [&_.MuiPaper-root]:!pl-2.5 [&_.MuiPaper-root]:!min-h-0 [&_.MuiPaper-root]:!h-[30px] [&_.MuiTypography-root]:!text-xs [&_.MuiSvgIcon-root]:!text-[16px]">
                        <CustomDateRangePicker
                          selectedRange={[parseISO(filters.fromDate), parseISO(filters.toDate)]}
                          onRangeChange={([from, to]) => {
                            if (Math.abs((to - from) / (1000 * 60 * 60 * 24)) > 90) { showToast("Date range cannot exceed 3 months", "error"); return; }
                            setFilters(prev => ({ ...prev, fromDate: from.toLocaleDateString("en-CA"), toDate: to.toLocaleDateString("en-CA") }));
                          }}
                        />
                      </div>
                    ) : (
                      <button type="button" onClick={() => setUseDateFilter(true)} className="w-full h-[30px] border-2 border-dashed border-gray-300 rounded-lg text-xs text-gray-600 hover:border-blue-400 hover:bg-blue-50/50 transition-all font-medium">+ Add Created Date Range</button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row flex-wrap gap-1.5 items-stretch sm:items-center">
                  <button type="submit" className="w-full sm:w-auto px-3 py-1.5 bg-gradient-to-r from-slate-600 to-slate-700 text-white rounded-lg shadow-md hover:shadow-lg hover:from-slate-700 hover:to-slate-800 transition-all font-semibold flex items-center justify-center gap-1.5 text-xs">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <span>Apply Filters</span>
                  </button>
                  <button type="button" onClick={resetFilters} className="w-full sm:w-auto px-3 py-1.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg shadow-md hover:shadow-lg hover:from-gray-600 hover:to-gray-700 transition-all font-semibold flex items-center justify-center gap-1.5 text-xs">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                    <span>Reset All</span>
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="m-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg flex items-center gap-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
              <span className="font-medium">{error}</span>
            </div>
          )}

          {/* ===== Table ===== */}
          <div className="w-full max-w-full overflow-x-auto">
            {loading ? <TableSkeleton /> : drafts.length === 0 ? <EmptyState tab={selectedTab} onReset={resetFilters} /> : (
              <table className="w-full table-auto">
                <thead className="bg-gradient-to-r from-slate-600 to-slate-700 text-white sticky top-0 z-10">
                  <tr>
                    <th className={`${viewMode === "compact" ? "p-1.5" : "p-3"} w-10`}>
                      <div className="flex items-center justify-center"><input ref={headerCheckboxRef} type="checkbox" checked={allOnPageSelected} onChange={(e) => toggleSelectAllOnPage(e.target.checked)} className="h-5 w-5 accent-slate-600 cursor-pointer rounded border-2 border-white" /></div>
                    </th>
                    {["Order Ref No.", "Pickup Address", "Delivery Address", "Payment", "Items", "Dimensions", "Status", "Courier", "Actions"].map((h, i) => (
                      <th key={i} className={`${viewMode === "compact" ? "p-1.5 text-xs" : "p-3"} text-left font-semibold tracking-wide ${h === "Actions" ? "sticky right-0 bg-slate-700 min-w-[120px] shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]" : ""} ${h === "Courier" ? "min-w-[160px]" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {drafts.map((d, i) => {
                    const checked = selectedRows.has(d.draft_order_id);
                    const isPlacing = placingIds.has(d.draft_order_id);
                    const isRetrying = retryingIds.has(d.draft_order_id);
                    const od = d.order_data || {};
                    const pickup = od.pickup_details || {};
                    const drop = od.drop_details || {};
                    const orderDetails = od.shipment_details?.order_details || {};
                    const firstPkg = od.shipment_details?.shipment_packages?.[0] || {};
                    const dims = firstPkg.dimensions || {};
                    const weight = firstPkg.weight || {};
                    const allItems = (od.shipment_details?.shipment_packages || []).flatMap(pkg => pkg.items || []);
                    const hasFailed = d.draft_status === "FAILED";
                    const hasError = hasFailed && d.placement_error;
                    const isErrorExpanded = expandedErrorLogs[d.draft_order_id];

                    // Calculate total amount from items
                    const totalAmount = allItems.reduce((sum, it) => sum + ((it.item_payment_details?.unit_price || 0) * (it.quantity || 1)), 0);
                    const paymentType = orderDetails.order_type || od.payment_mode || "";
                    const orderRefNumber = orderDetails.order_reference_number || d.order_source_reference_id || "";

                    return (
                      <React.Fragment key={d.draft_order_id}>
                        <tr className={`group relative transition-all ${checked ? "bg-slate-50" : "hover:bg-slate-50/50"} ${viewMode === "compact" ? "text-sm" : ""} ${isPlacing || isRetrying ? "opacity-60" : ""}`}>
                          <td className={`${viewMode === "compact" ? "p-1.5" : "p-3"} w-10`}>
                            <div className="flex items-center justify-center"><input type="checkbox" checked={checked} onChange={() => toggleSelectOne(d.draft_order_id)} className="h-5 w-5 accent-blue-600 cursor-pointer rounded" /></div>
                          </td>

                          {/* Order Ref / Date */}
                          <td className={viewMode === "compact" ? "p-1.5" : "p-3"}>
                            <div className="flex flex-col space-y-0.5">
                              <button onClick={() => setDetailDrawer({ open: true, draft: d })} className="text-blue-600 hover:text-blue-800 underline font-semibold text-left text-xs truncate max-w-[170px]" title={orderRefNumber || d.draft_order_id}>
                                {orderRefNumber || d.draft_order_id}
                              </button>
                              <span className="text-[10px] text-gray-400">{d.created_date_time ? formatInIST(d.created_date_time) : "\u2014"}</span>
                              {od.shipment_order_type && (
                                <span className={`inline-flex max-w-fit items-center px-1.5 py-0.5 rounded text-[9px] font-bold ${od.shipment_order_type === "REVERSE" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                                  {od.shipment_order_type}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Pickup Address */}
                          <td className={`${viewMode === "compact" ? "p-1.5" : "p-3"} max-w-[180px]`}>
                            {pickup.pickup_poc || pickup.pickup_address ? (
                              <div className="flex flex-col space-y-0.5">
                                <span className="text-xs font-medium text-gray-900 truncate">{pickup.pickup_poc || "\u2014"}</span>
                                <span className="text-[10px] text-gray-500 truncate" title={pickup.pickup_address}>{pickup.pickup_address ? (pickup.pickup_address.length > 40 ? pickup.pickup_address.slice(0, 40) + "..." : pickup.pickup_address) : "\u2014"}</span>
                                <span className="text-[10px] text-gray-400">{[pickup.pickup_city, pickup.pickup_state, pickup.pickup_postal_code].filter(Boolean).join(", ")}</span>
                              </div>
                            ) : <span className="text-xs text-gray-400">{"\u2014"}</span>}
                          </td>

                          {/* Delivery Address */}
                          <td className={`${viewMode === "compact" ? "p-1.5" : "p-3"} max-w-[180px]`}>
                            {drop.drop_poc || drop.drop_address ? (
                              <div className="flex flex-col space-y-0.5">
                                <span className="text-xs font-medium text-gray-900 truncate">{drop.drop_poc || "\u2014"}</span>
                                <span className="text-[10px] text-gray-500 truncate" title={drop.drop_address}>{drop.drop_address ? (drop.drop_address.length > 40 ? drop.drop_address.slice(0, 40) + "..." : drop.drop_address) : "\u2014"}</span>
                                <span className="text-[10px] text-gray-400">{[drop.drop_city, drop.drop_state, drop.drop_postal_code].filter(Boolean).join(", ")}</span>
                              </div>
                            ) : <span className="text-xs text-gray-400">{"\u2014"}</span>}
                          </td>

                          {/* Payment */}
                          <td className={viewMode === "compact" ? "p-1.5" : "p-3"}>
                            <div className="flex flex-col space-y-0.5">
                              <span className="font-bold text-gray-900 text-xs">{totalAmount > 0 ? `\u20B9${totalAmount.toLocaleString('en-IN')}` : "\u2014"}</span>
                              {paymentType && (
                                <span className={`inline-flex max-w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${paymentType === "COD" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>
                                  {paymentType}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Items */}
                          <td className={`${viewMode === "compact" ? "p-1.5" : "p-3"} max-w-[150px]`}>
                            {allItems.length > 0 ? (
                              <div className="flex flex-col space-y-0.5">
                                {allItems.slice(0, 2).map((it, idx) => (
                                  <span key={idx} className="text-xs text-gray-700 truncate" title={it.item_general_details?.item_name}>
                                    {it.item_general_details?.item_name || it.item_general_details?.sku || "\u2014"} × {it.quantity || 1}
                                  </span>
                                ))}
                                {allItems.length > 2 && <span className="text-[10px] text-gray-400">+{allItems.length - 2} more</span>}
                              </div>
                            ) : <span className="text-xs text-gray-400">{"\u2014"}</span>}
                          </td>

                          {/* Dimensions */}
                          <td className={viewMode === "compact" ? "p-1.5" : "p-3"}>
                            {(dims.length || weight.value) ? (
                              <div className="flex flex-col space-y-0.5 text-xs text-gray-700">
                                {dims.length && <span>{dims.length}×{dims.breadth}×{dims.height} {dims.unit || "cm"}</span>}
                                {weight.value && <span className="text-[10px] text-gray-500">{weight.value} {weight.unit || "gm"}</span>}
                              </div>
                            ) : <span className="text-xs text-gray-400">{"\u2014"}</span>}
                          </td>

                          {/* Status */}
                          <td className={viewMode === "compact" ? "p-1.5" : "p-3"}>
                            <div className="flex flex-col space-y-0.5">
                              <DraftStatusBadge status={d.draft_status} />
                              {d.draft_status === "PLACED" && d.placed_shipment_order_id && <span className="text-[10px] text-emerald-600 font-mono">{d.placed_shipment_order_id}</span>}
                              {hasError && <span className="text-[10px] text-rose-500 truncate max-w-[140px]" title={d.placement_error.error_message}>{parsePlacementErrorMessage(d.placement_error.error_message)}</span>}
                              {isPlacing && <span className="text-[10px] text-blue-500 flex items-center gap-1"><svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>Placing...</span>}
                              {/* Validation warnings for DRAFT orders */}
                              {(d.draft_status === "DRAFT") && draftValidationResults[d.draft_order_id]?.length > 0 && (
                                <div className="mt-0.5 group/val relative">
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-amber-200 cursor-help">
                                    <svg className="w-3 h-3 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                                    {draftValidationResults[d.draft_order_id].length} field{draftValidationResults[d.draft_order_id].length > 1 ? "s" : ""} missing
                                  </span>
                                  {/* Hover tooltip showing missing fields */}
                                  <div className="hidden group-hover/val:block absolute left-0 top-full mt-1 z-50 bg-white border border-amber-200 rounded-lg shadow-xl p-2.5 min-w-[200px] max-w-[280px]">
                                    <p className="text-[10px] font-bold text-gray-700 mb-1.5 uppercase tracking-wider">Missing Mandatory Fields</p>
                                    <ul className="space-y-0.5">
                                      {draftValidationResults[d.draft_order_id].map((err, ei) => (
                                        <li key={ei} className="text-[11px] text-rose-600 flex items-start gap-1">
                                          <svg className="w-3 h-3 text-rose-400 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                                          <span><span className="text-gray-500">{err.section}:</span> {err.field}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Courier */}
                          <td className={viewMode === "compact" ? "p-1.5" : "p-3"}>
                            {(() => {
                              const draftId = d.draft_order_id;
                              const isEligible = d.draft_status === "DRAFT" || d.draft_status === "FAILED";
                              const loadingCourier = loadingCarrierKeys.has(draftId);
                              const courierOptions = carrierOptionsByKey[draftId] || [];
                              const selectedCarrierName = carrierSelectionByKey[draftId];
                              const existingCarrier = d.order_data?.shipment_details?.shipment_carrier_details?.carrier_partner_public_name;

                              return (
                                <div data-carrier-dropdown-root={draftId} className="flex flex-col gap-1 text-left min-w-0">
                                  {/* Existing carrier display */}
                                  {existingCarrier && !courierOptions.length && !loadingCourier && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 truncate max-w-[140px]" title={existingCarrier}>
                                      {existingCarrier}
                                    </span>
                                  )}

                                  {isEligible && (
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <button
                                        type="button"
                                        onClick={() => performCourierFetchForDraft(d)}
                                        disabled={loadingCourier}
                                        className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        title="Fetch courier prices for this order"
                                      >
                                        {loadingCourier ? "Fetching..." : "Get Prices"}
                                      </button>
                                      {loadingCourier && (
                                        <svg className="animate-spin h-3 w-3 text-slate-500" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                      )}
                                    </div>
                                  )}

                                  {!loadingCourier && courierOptions.length > 0 ? (
                                    <div className="relative">
                                      <button
                                        ref={carrierDropdownOpenKey === draftId ? carrierTriggerRef : undefined}
                                        type="button"
                                        onClick={() => setCarrierDropdownOpenKey(prev => (prev === draftId ? null : draftId))}
                                        className="w-full text-left flex items-center justify-between gap-1 px-1.5 py-1 rounded border border-slate-200 bg-white text-slate-800 text-[10px] font-medium hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500 min-h-0"
                                      >
                                        <span className="min-w-0 break-words text-left">
                                          {!selectedCarrierName
                                            ? "Use recommendation"
                                            : (() => {
                                                const sel = courierOptions.find(c => c.name === selectedCarrierName);
                                                return sel ? `${sel.name} · \u20B9${Number(sel.price).toFixed(0)}` : selectedCarrierName;
                                              })()}
                                        </span>
                                        <svg className={`h-3 w-3 text-slate-400 shrink-0 transition-transform ${carrierDropdownOpenKey === draftId ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0l-4.24-4.24a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                                        </svg>
                                      </button>

                                      {carrierDropdownOpenKey === draftId && ReactDOM.createPortal(
                                        <div
                                          ref={carrierMenuRef}
                                          className="fixed z-[99999] py-1 rounded-lg bg-white border border-slate-200 shadow-lg overflow-hidden"
                                          style={{
                                            left: carrierDropdownPosition.left,
                                            width: carrierDropdownPosition.width,
                                            ...(carrierDropdownPosition.openUp
                                              ? { bottom: carrierDropdownPosition.bottom }
                                              : { top: carrierDropdownPosition.top }),
                                          }}
                                        >
                                          <div className="max-h-[270px] overflow-y-auto">
                                            <button
                                              type="button"
                                              onClick={() => handleCourierSelect(draftId, null)}
                                              className={`w-full text-left px-2.5 py-2 text-sm transition-colors ${!selectedCarrierName ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-600 hover:bg-slate-50"}`}
                                            >
                                              Use recommendation
                                            </button>
                                            {courierOptions.map(opt => {
                                              const expectedBy = formatExpectedDeliveryIST(opt.max_tat_in_days);
                                              const isSelected = selectedCarrierName === opt.name;
                                              return (
                                                <button
                                                  key={opt.id}
                                                  type="button"
                                                  onClick={() => handleCourierSelect(draftId, opt.name)}
                                                  className={`w-full text-left px-2.5 py-2 text-sm transition-colors flex items-center justify-between gap-2 ${isSelected ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-600 hover:bg-slate-50"}`}
                                                >
                                                  <span className="min-w-0" title={opt.name}>{opt.name}</span>
                                                  <span className="shrink-0 tabular-nums">{"\u20B9"}{Number(opt.price).toFixed(0)} · {expectedBy}</span>
                                                </button>
                                              );
                                            })}
                                          </div>
                                        </div>,
                                        document.body
                                      )}

                                      {selectedCarrierName && (() => {
                                        const sel = courierOptions.find(c => c.name === selectedCarrierName);
                                        if (!sel) return null;
                                        return <div className="mt-0.5 text-[9px] text-slate-500">By {formatExpectedDeliveryIST(sel.max_tat_in_days)} (IST)</div>;
                                      })()}
                                    </div>
                                  ) : !loadingCourier && !courierOptions.length && !existingCarrier ? (
                                    <span className="text-[10px] text-slate-400">{"\u2014"}</span>
                                  ) : null}
                                </div>
                              );
                            })()}
                          </td>

                          {/* Actions */}
                          <td className={`${viewMode === "compact" ? "p-1.5" : "p-3"} sticky right-0 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)] ${checked ? "bg-slate-50" : "bg-white group-hover:bg-slate-50/50"}`}>
                            <div className="flex items-center gap-1">
                              {hasError && (
                                <button
                                  onClick={() => setExpandedErrorLogs(prev => ({ ...prev, [d.draft_order_id]: !prev[d.draft_order_id] }))}
                                  className={`px-2 py-1 text-[10px] font-semibold rounded-md border transition-all flex items-center gap-1 ${isErrorExpanded ? "text-rose-800 bg-rose-100 border-rose-300" : "text-rose-600 bg-rose-50 border-rose-200 hover:bg-rose-100"}`}
                                  title="View error details"
                                >
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                  Details
                                  <svg className={`w-3 h-3 transition-transform ${isErrorExpanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </button>
                              )}
                              {hasFailed && (
                                <button
                                  onClick={() => handleRetryPlace(d)}
                                  disabled={isRetrying}
                                  className={`px-2 py-1 text-[10px] font-semibold rounded-md border transition-all flex items-center gap-1 ${isRetrying ? "text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed" : "text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100"}`}
                                  title="Retry placing this order"
                                >
                                  {isRetrying ? (
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                  ) : (
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                  )}
                                  Retry
                                </button>
                              )}
                              <ActionMenu draft={d} onAction={handleActionClick} onOpenChange={() => {}} validationErrors={draftValidationResults[d.draft_order_id] || []} />
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Error Details Row (FAILED drafts only) */}
                        {hasError && isErrorExpanded && (
                          <tr className="bg-rose-50/60">
                            <td colSpan={10} className="p-0">
                              <div className="px-4 py-3">
                                <h4 className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                                  <svg className="w-3.5 h-3.5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                  Placement Error Details
                                </h4>
                                <div className="overflow-hidden rounded-lg border border-rose-200">
                                  <table className="w-full text-xs">
                                    <thead className="bg-rose-100 text-gray-700">
                                      <tr>
                                        <th className="px-3 py-1.5 text-left font-semibold">Error Code</th>
                                        <th className="px-3 py-1.5 text-left font-semibold">Error Message</th>
                                        <th className="px-3 py-1.5 text-left font-semibold">Retryable</th>
                                        <th className="px-3 py-1.5 text-left font-semibold">Failed At</th>
                                        {d.placement_error.failed_carrier_partner_id && <th className="px-3 py-1.5 text-left font-semibold">Carrier</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr className="hover:bg-rose-50">
                                        <td className="px-3 py-2 font-mono text-rose-700 font-semibold">{d.placement_error.error_code || "\u2014"}</td>
                                        <td className="px-3 py-2 max-w-[350px]">
                                          <div className="text-gray-900 font-medium">{parsePlacementErrorMessage(d.placement_error.error_message)}</div>
                                          {d.placement_error.error_message && d.placement_error.error_message.length > 60 && (
                                            <details className="mt-1">
                                              <summary className="text-[10px] text-gray-400 cursor-pointer hover:text-gray-600">Show raw error</summary>
                                              <pre className="mt-1 text-[10px] text-gray-400 whitespace-pre-wrap break-all font-mono bg-gray-50 rounded p-1.5 max-h-24 overflow-y-auto">{d.placement_error.error_message}</pre>
                                            </details>
                                          )}
                                        </td>
                                        <td className="px-3 py-2">
                                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${d.placement_error.retryable ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                            {d.placement_error.retryable ? "Yes" : "No"}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-gray-500">{d.placement_error.failed_at ? formatInIST(d.placement_error.failed_at) : d.updated_date_time ? formatInIST(d.updated_date_time) : "\u2014"}</td>
                                        {d.placement_error.failed_carrier_partner_id && <td className="px-3 py-2 font-medium text-gray-800">{d.placement_error.failed_carrier_partner_id}</td>}
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>

                                {/* Additional placement attempt history if available */}
                                {d.placement_attempts && d.placement_attempts.length > 0 && (
                                  <div className="mt-3">
                                    <h4 className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                                      <svg className="w-3.5 h-3.5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                      Placement Attempt History
                                    </h4>
                                    <div className="overflow-hidden rounded-lg border border-rose-200">
                                      <table className="w-full text-xs">
                                        <thead className="bg-rose-100 text-gray-700">
                                          <tr>
                                            <th className="px-3 py-1.5 text-left font-semibold">#</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Carrier</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Status</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Error Code</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Error Message</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Timestamp</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-rose-100">
                                          {d.placement_attempts.map((attempt, aidx) => (
                                            <tr key={aidx} className="hover:bg-rose-50">
                                              <td className="px-3 py-1.5 text-gray-500">{aidx + 1}</td>
                                              <td className="px-3 py-1.5 font-medium text-gray-800">{attempt.carrier_partner_id || attempt.error_provider || "\u2014"}</td>
                                              <td className="px-3 py-1.5">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${attempt.success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                                  {attempt.success ? "Success" : "Failed"}
                                                </span>
                                              </td>
                                              <td className="px-3 py-1.5 font-mono text-gray-600">{attempt.error_code || "\u2014"}</td>
                                              <td className="px-3 py-1.5 text-gray-600 max-w-[250px] truncate" title={attempt.error_message}>{parsePlacementErrorMessage(attempt.error_message) || "\u2014"}</td>
                                              <td className="px-3 py-1.5 text-gray-500">{attempt.timestamp ? formatInIST(attempt.timestamp) : "\u2014"}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {/* Quick retry action from expanded section */}
                                <div className="mt-3 flex items-center gap-2">
                                  <button
                                    onClick={() => handleRetryPlace(d)}
                                    disabled={isRetrying}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 shadow-sm ${isRetrying ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white hover:shadow-md"}`}
                                  >
                                    {isRetrying ? (
                                      <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                    ) : (
                                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                    )}
                                    {isRetrying ? "Retrying placement..." : "Retry Placement"}
                                  </button>
                                  <button
                                    onClick={() => {
                                      const normalized = normalizeDraftOrderData(d.order_data);
                                      // For failed drafts, append _1, _2, etc. to order_reference_number
                                      if (d.draft_status === "FAILED" && normalized.order_reference_number) {
                                        const ref = normalized.order_reference_number;
                                        const suffixMatch = ref.match(/_(\d+)$/);
                                        if (suffixMatch) {
                                          normalized.order_reference_number = ref.replace(/_(\d+)$/, `_${parseInt(suffixMatch[1], 10) + 1}`);
                                        } else {
                                          normalized.order_reference_number = `${ref}_1`;
                                        }
                                      }
                                      const isReverse = (d.order_data?.shipment_order_type || "").toUpperCase() === "REVERSE";
                                      navigate(isReverse ? "/create-reverse-order" : "/create-forward-order", { state: { cloneFrom: normalized, draftOrderId: d.draft_order_id } });
                                    }}
                                    className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                    Edit Draft & Retry
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!loading && drafts.length > 0 && (
            <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100/50 p-2 md:p-3">
              <div className="flex flex-wrap justify-between items-center gap-2">
                <div className="flex items-center gap-2">
                  <button onClick={prevPage} disabled={currentPage === 1} className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg font-semibold text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm hover:shadow flex items-center gap-1.5 text-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>Previous
                  </button>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg">
                    <span className="text-sm text-gray-600">Page</span>
                    <span className="font-bold text-blue-600 text-lg">{currentPage}</span>
                    {totalPages > 1 && <><span className="text-gray-400">of</span><span className="font-bold text-gray-700">{totalPages}</span></>}
                  </div>
                  <button onClick={nextPage} disabled={drafts.length < limit} className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg font-semibold text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm hover:shadow flex items-center gap-1.5 text-sm">
                    Next<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Showing {((currentPage - 1) * limit) + 1}{"\u2013"}{Math.min(currentPage * limit, totalElements)} of {totalElements}</span>
                  <select value={limit} onChange={handleLimitChange} className="px-2 py-1.5 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm cursor-pointer text-sm">
                    {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
